import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import {
  InformationPackageApiProvider,
  useInformationPackageApi,
} from '../Services/InformationPackageApiProvider';
import type { InformationPackageArtifactApi } from '../Services/InformationPackageApiProviderTypes';

import { Tab, Tabs } from '../Components/Tabs';
import UnitArtifactContent from './UnitArtifactContent';
import UnitArtifactDetails from './UnitArtifactDetails';
import InformationPackageFeaturesTable from '../Components/InformationPackageFeaturesTable';

interface InformationPackageArtifactProps {
  canEdit?: boolean;
}

type ArtifactState = InformationPackageArtifactApi & {
  detail: string;
  content?: string | null;
};

const emptyArtifact: ArtifactState = {
  key: '',
  created_at: '',
  updated_at: '',
  is_active: false,
  artifact_type: '',
  summary: '',
  mime: '',
  detail: '',
  content: undefined,
};

function InformationPackageArtifactContent() {
  const [artifact, setArtifact] = useState<ArtifactState>(emptyArtifact);
  const [isArtifactLoading, setIsArtifactLoading] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const navigate = useNavigate();
  const { organizationId, informationPackageId, unitId, artifactItemId } = useParams();
  const { showAlert } = useAlert();

  const { getInformationPackageArtifactById, getArtifactContentById } = useInformationPackageApi();

  const fetchArtifact = async () => {
    setIsArtifactLoading(true);

    try {
      const response = await getInformationPackageArtifactById(artifactItemId as string);

      setArtifact({
        ...response,
        detail: response.artifact_type,
        summary: response.summary ?? '',
        content: undefined,
      });
    } catch {
      showAlert('fail', 'Failed to load artifact');
    } finally {
      setIsArtifactLoading(false);
    }
  };

  useEffect(() => {
    fetchArtifact();
  }, [artifactItemId]);

  useEffect(() => {
    if (activeTab !== 'content' || artifact.content !== undefined) {
      return;
    }

    const fetchArtifactContent = async () => {
      setIsContentLoading(true);

      try {
        const artifactContent = await getArtifactContentById(artifactItemId as string);
        setArtifact((prev) => ({ ...prev, content: artifactContent.content }));
      } catch {
        showAlert('fail', 'Failed to load artifact content');
      } finally {
        setIsContentLoading(false);
      }
    };

    fetchArtifactContent();
  }, [activeTab, artifact.content, artifactItemId, getArtifactContentById, showAlert]);

  const updateArtifactField = (field: keyof ArtifactState, value: string) => {
    setArtifact((prev) => ({ ...prev, [field]: value }));
  };

  const onCancel = () => {
    navigate(
      `/organizations/${organizationId}/information-package/${informationPackageId}/unit/${unitId}`,
    );
  };

  if (isArtifactLoading || isContentLoading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">Artifact</h2>

          <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
            <Tab label="Details" value="details">
              <div className="d-flex flex-column gap-12 w-100">
                <UnitArtifactDetails artifact={artifact} onChangeField={updateArtifactField} />

                <InformationPackageFeaturesTable
                  unitId={unitId as string}
                  artifactKey={artifactItemId as string}
                  errorMessage="Failed to load artifact features"
                />
              </div>
            </Tab>

            <Tab label="Content" value="content">
              <UnitArtifactContent artifact={artifact} />
            </Tab>
          </Tabs>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={true}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InformationPackageArtifact(_props: InformationPackageArtifactProps) {
  return (
    <InformationPackageApiProvider>
      <InformationPackageArtifactContent />
    </InformationPackageApiProvider>
  );
}

export default InformationPackageArtifact;
